import {
  Component,
  Element,
  Event,
  type EventEmitter,
  Fragment,
  h,
  Host,
  type JSX,
  Listen,
  Method,
  Prop,
  Watch,
  State
} from '@stencil/core';
import {makeLogger} from '../../logger/logger';
import {defaultLabelProvider, type labelProviderInterface} from '../LabelProviderInterface';
import {z} from 'zod';
import {GuidedDataFlux} from './flux';
import DataFlux from '../../utils/libs/dataflux/dataflux';
import {GuidedV2Defaults, GuidedV2StrategyService} from '../strategies/GuidedV2StrategyService';
import {StrategiesFactory} from '../strategies/Strategies';
import {GUIDED_V2_SERVICE} from '../strategies/utils/variables';
import type {
  GuidedSearchCompleted,
  GuidedSearchHighlighting,
  GuidedSearchLoaded,
  GuidedSearchUserTyping
} from '../guided/api';
import {type GuidedV2Emitters} from './models/eventNames';
import {addUserAudiencesAndRedirect, debounce, getUserTypes, isTargetInput} from '../guided/subcomponents/utils';
import {guidedMiddlewareRegistry} from './middlewares/_registry';
import {GuidedUiElementFactory} from './subcomponents/ui-components';
import {ChipType, type GuidedChipEventDetail,type HistoryEventDetail} from './subcomponents/chips-carousel/model';
import type {GuidedV2SubcomponentUiProvider, GuidedV2UiSection} from './models/ui';
import type {GuidedMiddleware, GuidedMiddlewareRegistry} from './models/middleware';
import type {ChipItem} from './subcomponents/chips-carousel/guided-v2-chips-carousel';
import {GuidedV2ResultListSection} from './subcomponents/result-list-section/result-list';
import {isMobile} from '../../utils/media';
import { SelectedSuggestions } from './subcomponents/selected-suggestions/selected-suggestions';
import { promotedData } from './utils';


const {info, debug, warn, err} = makeLogger({pathToCurrentFile: 'components/guided2/uiaas-guided-search-v2.tsx'});

@Component({
  tag: 'uiaas-guided-search-v2',
  scoped: false,
  styleUrl: 'uiaas-guided-search-v2.scss',
})
export class UiaasGuidedSearchV2 {

  @Element() element: HTMLElement;


  /**
   * Accepts stringified JSON with labels, the value gets JSON.parse and is expected to be Record<string,string> after that
   */
  @Prop() readonly labels: string;
  private _parsedLabels: Record<string, string> | undefined = undefined;
  /**
   * The label provider to use, defaults to a simple fallback returning fallback string all the time.
   */
  @Prop() readonly labelProvider: labelProviderInterface = (label, fallback) => {
      if(!this._parsedLabels){try{
        this._parsedLabels = JSON.parse(this.labels || '{}');
      }catch(e){
        warn('Failed to parse labels JSON', e);
        this._parsedLabels = {};
      }
      return this._parsedLabels[label] || fallback;
      }
  };

  /**
   * List of all registered middlewares - that can be used as backends
   */
  @Prop() readonly middlewareRegistry: GuidedMiddlewareRegistry = {
    ...guidedMiddlewareRegistry
  };

  private strategy: GuidedV2StrategyService;

  /**
   * Which section should be opened when user clicks the search bar?
   * For example when user is on P&A page, we should initially open the P&A section of Guided
   */
  @Prop() readonly initialSection?: string;
  @Watch('initialSection')
  initialSectionChanged(newValue: string, _oldValue: string) {
      info('Setting opened section, via props, to', newValue);
      this.dataFlux.root.openedSection = newValue;
  }

  /**
   * Register a middleware to be used as backend
   * @param name
   * @param middleware
   */
  @Method()
  async registerMiddleware(name: string, middleware: GuidedMiddleware): Promise<void> {
      // console.log('registering middleware', name, middleware);
      this.middlewareRegistry[name] = async () => middleware;
  }

  /**
   * Trigger the query to all backends. Force reload of the data.
   */
  @Method()
  async triggerQuery() {

      const startedAt = performance.now();
      this.dataFlux.root.requestInProgress = {
        isRunning: true,
        startedAt,
        finishedAt: undefined,
      };
      this.dataFlux.root.results = [];

      const backends = Array.from(this.element
        .querySelectorAll('*'))
        .filter(tag => tag.tagName
          .toLowerCase()
          .startsWith('uiaas-guided-search-v2-backend')
        );

      // console.log('Using Guided backends', backends);

      const results = await Promise.allSettled(
        backends.map(async backend => {
            if ('fetch' in backend && backend.fetch instanceof Function) {
              try {
                return [null, backend, await backend.fetch(
                  this.dataFlux,
                  this.middlewareRegistry,
                )];
              } catch (e) {
                warn('Guided backend failed with error', backend, e);
                return [new Error('Guided backend failed'), backend, null];
              }
            } else {
              warn('Guided backend has no "fetch" method', backend);
              return [new Error('Guided backend failed - no fetch method'), backend, null];
            }
        })
      ).then((results) => {

          // console.log('Guided backend full call report', results);

          results.filter(r => r.status === 'rejected').forEach(r => {
              err('Guided backend - promise rejected', r);
          });
          return results.filter(r => r.status === 'fulfilled')
            .map(r => r.value)
            .map(([_, backend, results]) => {
                info('Guided backend results success', {backend, results});
                return results;
            });
      });

      // console.log('Finally guided has results:', results);

      this.dataFlux.root.results = results.flat();

      const finishedAt = performance.now();

      this.dataFlux.root.requestInProgress = {
        ...this.dataFlux.root.requestInProgress,
        isRunning: false,
        finishedAt,
      };
      this.eventEmitterGuidedSearchLoaded.emit({
        term: this.dataFlux.root.searchTerm,
        duration_ms: finishedAt - startedAt,
      });
  }


  private readonly dataFlux: DataFlux<GuidedDataFlux> = new DataFlux(new GuidedDataFlux());

  /**
   * Open the guided search panel in imperative way
   */
  @Method()
  public async togglePane(open) {
      this.showPaneShadow = false;
      this.dataFlux.root.opened = !!open;
      this.dataFlux.root.mouseHoversGuidedPanel = !!open;
  }

  /**
   * Gets the dataflux object, for low level access to the internal data structures
   */
  @Method()
  public async getDatafluxUnsafe() {
      return this.dataFlux;
  }
  /**
   * Search term, to imperatively provide by external component
   */
  @Prop()
  readonly searchTerm: string = '';

  @Prop() readonly personalizedContent: boolean = false;
  @Prop() readonly supportedAudiences: string = '';
  @Prop() readonly unifiedCheckout: boolean = false;
  @State() mobileView: boolean = isMobile();
  @State() showPaneShadow: boolean = false;
  private userTypes = [];
  private audiences: string[] = [];

  @Listen('resize', {target: 'window'})
  handleResize() {
      this.mobileView = isMobile();
  }

  private mouseInPane = () => {
      this.dataFlux.root.mouseHoversGuidedPanel = true;
  };

  private mouseOutPane = () => {
      this.dataFlux.root.mouseHoversGuidedPanel = false;
  };

  private scrollPane = (ev) => {
      this.showPaneShadow = ev.target.scrollTop > 2;
  };

  @Watch('searchTerm')
  searchTermChanged(newValue: string, _oldValue: string) {
      this.dataFlux.root.searchTerm = newValue;
  }

  private updateRtlStatus() {
      const rtlTags = [
        this.element,
        document.querySelector('html'),
        document.querySelector('body'),
      ];
      for (const tag of rtlTags) {
        const dir = tag.getAttribute('dir');
        if (dir) {
          if (dir === 'rtl') {
            this.dataFlux.root.rightToLeft = true;
            return;
          }
        }
      }
      this.dataFlux.root.rightToLeft = false;
  }

  /**
   * List of all event handlers, to be removed when component is disconnected,
   * stored as callbacks that can be called to remove the event listeners
   * @private
   */
  private eventHandlers: Array<() => void> = [];


  #getVisibleSections(){
      const sections = this.strategy.details.schema.sections;
      //do a quick validation of the sections
      sections.forEach(section => {
          if (section == null) {
            err('Section is null', sections);
          }
          if (section?.showWhen == null) {
            err('Section.showWhen is null', section);
          }
      });

      return sections
        .filter(section => section != null)
        .filter(section => this.dataFlux.root.checkVisibility(section.showWhen));
  }


  #getVisibleUiElements(section) {
      const uiElements = (section.uiElements || []).filter(Boolean);

      if (!uiElements) {
        err('UI elements are null', section);
        return [];
      }

      uiElements.forEach(element => {
          if (!element.showWhen) {
            err('UI element.showWhen is null', { element, section });
          }
      });

      const matchesResult = (element) =>
        element.itemSources?.some(itemSource =>
          this.dataFlux.root.results?.some(result =>
            result.category === itemSource.category
          )
        );

      const visibleElementsWithLimit = [];
      let quickLinkCounter = 0;

      for (const element of uiElements) {
        const isVisible = this.dataFlux.root.checkVisibility(element.showWhen);
        const shouldAdd = isVisible &&
        (!element.itemSources || element.type === 'no-links-message' || element.type === 'no-result-message' || matchesResult(element));

        if (shouldAdd) {
          if (element.type === 'quick-links') {
            if (quickLinkCounter < 3) {
              visibleElementsWithLimit.push(element);
              quickLinkCounter++;
            }
          } else {
            visibleElementsWithLimit.push(element);
          }
        }
      }

      // Remove the last element if its type is 'divider'
      if (visibleElementsWithLimit.at(-1)?.type === 'divider') {
        visibleElementsWithLimit.pop();
      }

      return visibleElementsWithLimit;
  }

  #guidedSection = (section: GuidedV2UiSection) => {
      return <div class="guided-v2-section">
        <div class="guided-v2-section__content" data-section-name={section.name}>
          {this.#getVisibleUiElements(section)
            .map((element) => {
                return GuidedUiElementFactory(
                  element,
                  {
                    eventEmitters: this.eventEmitters,
                    labelProvider: this.labelProvider,
                    searchTerm: this.dataFlux.root.searchTerm,
                    highlightedIndex: this.dataFlux.root.highlightedIndex,
                    results: this.dataFlux.root.results,
                    strategyLinkRenderer: this.strategy.getQuickLinkTemplateMapper({
                      eventEmitters: this.eventEmitters,
                      labelProvider: this.labelProvider
                    }) as GuidedV2SubcomponentUiProvider,
                    badgeHighlightThreshold: this.strategy.details.badgeHighlightThreshold,
                    markupHighlightThreshold: this.strategy.details.markupHighlightThreshold,
                    setSearchTermCallback: (term: string) => {
                        this.dataFlux.root.searchTerm = term;
                    },
                    isMobile: this.mobileView,
                    unifiedCheckout: this.unifiedCheckout,
                  }
                );
            })}
        </div>
      </div>;
  };

  @Listen('guided:chipClicked')
  carouselChipClicked(event: CustomEvent<GuidedChipEventDetail>) {
      const eventDetails = event.detail;

      debug('Chip in carousel clicked', eventDetails);

      if (eventDetails.sourceType == 'suggestions') {
        this.dataFlux.root.searchTerm = eventDetails.baseName;
        this.element.querySelectorAll('qds-input,input[type="search"]').forEach(input => {
            (input as HTMLInputElement).value = eventDetails.baseName;
            const form = input.closest('form');
            input.dispatchEvent(new CustomEvent('qdsInput'));
            if (input.tagName === 'QDS-INPUT') {
              (form || input).dispatchEvent(new CustomEvent('submit', {bubbles: true, composed: true, cancelable: true}));
            } else {
              form?.submit();
            }

        });
      } else {
        this.dataFlux.root.openedSection = eventDetails.baseName;
      }
  }

  @Listen('guidedV2:HistoryClicked')
  historyItemClicked(event:CustomEvent<HistoryEventDetail>) {
      const eventDetails = event.detail;
      const term = eventDetails.term;
      this.dataFlux.root.searchTerm = term;
      this.element.querySelectorAll('qds-input,input[type="search"]').forEach(input=>{
          (input as HTMLInputElement).value = term;
          const form = input.closest('form');
          input.dispatchEvent(new CustomEvent('qdsInput'));
          (form || input).dispatchEvent(new CustomEvent('submit', {bubbles:true, composed:true, cancelable:true}));
      });
  }

  #guidedPane = () => {

      //TODO: implement guided section limiting algo
      //that will allow more items in one section than
      //provided number of slots, when other sections are
      //empty. Similar mechanism is already in old guided

      /*
    Iterate over sections, assigning items to sections matching total number of slots
    If we have free slots, assign more elements by round robin,
    then tell each section how many items it can have

        => override default "slots" attribute
     */

      const visibleSections = this.#getVisibleSections();

      const sectionNames = visibleSections.map(section => section.name);

      const {country, language} = this.strategy.details;

      const promotedPopularSearchers = promotedData.popularSearchers?.[country]?.[language] || null;

      const promotedSelectedSuggestions = promotedData.selectedSuggestions?.[country]?.[language] || null;

      const isPromotedChips = !this.dataFlux.root.searchTerm && promotedPopularSearchers?.length > 0;

      const chips: ChipItem[] = isPromotedChips
        ? promotedPopularSearchers
        : sectionNames.map(name => ({
          label: this.labelProvider(
            `guided-v2.section-name.${name?.toLowerCase()}`,
            name
          ),
          name
        } satisfies ChipItem));

      let selectedSection = this.dataFlux.root.openedSection;
      try {
        if (!sectionNames.includes(selectedSection))
          selectedSection = sectionNames[0];
      } catch (ignored) {

      }

      const filterByLabel = this.labelProvider(
        'guided-v2.filter-by-label.smallcaps',
        'Filter by'
      );

      if (visibleSections.length == 0) {
        if (!this.dataFlux.root.searchTerm && promotedPopularSearchers?.length > 0) {
          this.eventEmitterGuidedSearchSearchBarClicked.emit();
          return [
            <uiaas-guided-v2-chips-carousel
              class="guided-v2-subcomponent-selected-suggestions"
              terms={chips}
              header={this.labelProvider('guided-v2.popular-searchers.label', 'Popular Searches')}
              isSelectable={false}
              chipType={ChipType.GuidedV2Suggestions}
              isMobile={this.mobileView}
              isPromoted={true}
              chipClickedSourceType={'suggestions'}
              country={country}
              language={language}
            ></uiaas-guided-v2-chips-carousel>,
            (promotedPopularSearchers?.length > 0 && promotedSelectedSuggestions?.length > 0) &&
            (<qds-divider class="guided-v2-promoted-divider"></qds-divider>),
            promotedSelectedSuggestions?.length > 0 && (<SelectedSuggestions
              results={promotedSelectedSuggestions}
              label={this.labelProvider('guided-v2.featured-suggestions.label', 'Featured Suggestions')}
              eventEmitters={this.eventEmitters}
            />)
          ].filter(Boolean);
        } else {
          return [];
        }
      }

      return [
        visibleSections.length > 1 ? <div class="filter-by-section add-selector-strength">
          <uiaas-guided-v2-chips-carousel
            terms={chips}
            header={filterByLabel}
            isSelectable={true}
            initialSelection={selectedSection ?? chips?.[0]?.name ?? ''}
            chipType={ChipType.GuidedV2Sections}
            isMobile={this.mobileView}
          ></uiaas-guided-v2-chips-carousel>
        </div>
          : '',
        visibleSections
          .filter(section => section.name === selectedSection)
          .map(this.#guidedSection)
      ];
  };

  #withBackdrop = (guided: JSX.Element) => {
      const useBackdrop = this.strategy.details.useBackdrop;
      const paneVisible = this.strategy.details.paneVisible;
      const opened = this.dataFlux.root.opened;
      const showBackdrop = opened && useBackdrop;

      return {
        hostClasses: {
          'guided-v2-host__input-active': showBackdrop
        }, markup: <Fragment>


          {useBackdrop ?
            <div
              class={{
                'guided_v2__backdrop': !paneVisible,
                'guided_v2__backdrop-pane-visible': paneVisible,
                'guided_v2__backdrop--visible': showBackdrop
              }}
              onClick={this.removeBackdrop}></div> : ''
          }
          {(useBackdrop)
            ? <div class="guided-v2-backdrop-fix">{guided}</div>
            : {guided}
          }
        </Fragment>
      };
  };


  render() {

      // console.log('Guided V2 render');
      const opened = this.dataFlux.root.opened;

      const guidedPane = opened ? this.#guidedPane() : <Fragment></Fragment>;

      const guidedPaneMarkup = (opened && guidedPane.length > 0) ? <div
        class={{
          'guided-v2-pane add-selector-strength': true,
          'pane-visible': this.strategy.details.paneVisible,
          'guided-v2-pane--shadow': this.showPaneShadow
        }}
        onMouseEnter={this.mouseInPane}
        onMouseLeave={this.mouseOutPane}
        onScroll={this.scrollPane}
      >{guidedPane}</div> : <Fragment></Fragment>;

      const {hostClasses, markup} = this.#withBackdrop(guidedPaneMarkup);

      hostClasses['add-selector-strength'] = true;

      const dir = this.dataFlux.root.rightToLeft ? 'rtl' : 'auto';

      const hiddenstyles = {
        'height': '0',
        'width': '0',
        'overflow': 'hidden',
        'opacity': '0',
        'pointer-events': 'none',
      };


      return <Host class={{
        ...hostClasses
      }}>
        <div class="guided-v2-wrapper" dir={dir}>
          <slot/>
          {markup}
        </div>
        <div style={hiddenstyles} id={'guided2-preloader'}>
          {this._fakeUi()}
        </div>
      </Host>;
  }

  /**
   * Purpose of fakeUi is to preload components used by Guided, so that when user uses guided - UI shows faster, with less time for FOUC
   */
  private _fakeUi = () => {
      const chips: ChipItem[] = [
        {name: 'aaa', label: 'aaa'}
      ];
      return [
        <qds-chip title="all results" basename="all results" icon-name=""></qds-chip>,
        <uiaas-guided-v2-chips-carousel
          terms={chips}
          isSelectable={true}
          initialSelection={chips[0].name}
        ></uiaas-guided-v2-chips-carousel>,
        <GuidedV2ResultListSection
          resultGroupOpenUrl={'#'}
          resultGroupName={'Test'}
          eventEmitters={
            this.eventEmitters
          }
          strategyLinkRenderer={this.strategy.getQuickLinkTemplateMapper({
            eventEmitters: this.eventEmitters,
            labelProvider: this.labelProvider
          })}
          links={[
            {
              score: 0,
              template: 'default',
              details: {

                quickLinkOpenUrl: '#', quickLinkTitle: 'test',
              },
            }
          ]}
          highlightedIndex={1}
        ></GuidedV2ResultListSection>
      ];
  };


  private keyDownHandler(event: KeyboardEvent) {
      if (isTargetInput(event)) {
      }
      // /* eslint-disable-next-line no-console */
      // console.log('keyDownHandler', _event);
  }


  private keyUpHandler(event: KeyboardEvent) {
      // /* eslint-disable-next-line no-console */
      // console.log('keyUpHandler', event);

      const sections = this.strategy.details.schema.sections;
      const visibleSections = this.#getVisibleSections();
      const currentSection = this.dataFlux.root.openedSection;
      const currentIndex = visibleSections.findIndex(section => section.name === currentSection);

      switch (event.key) {
      case 'Control':
      case 'Shift':
        break;
      case 'ArrowUp':
        event.preventDefault();
        event.stopPropagation();
        this.decreaseHighlightIndex();
        break;
      case 'ArrowDown':
        event.preventDefault();
        event.stopPropagation();
        this.increaseHighlightIndex();
        break;

      case 'ArrowLeft': //TODO: switch to section to the left
      // TODO: if opened section is popular keywords / suggestions, then switch highlight not tab

        if (currentIndex == -1) {//no section is visible, do nothing
        } else if (currentIndex == 0) {
        //open last section to the right
          const lastSection = visibleSections[visibleSections.length - 1];
          this.dataFlux.root.openedSection = lastSection.name;
        } else {
        //open the section to the left of currently opened
          const nextSection = visibleSections[currentIndex - 1];
          this.dataFlux.root.openedSection = nextSection.name;
        }
        break;

      case 'ArrowRight':
      //TODO: switch to section to the right,
      // TODO: if opened section is popular keywords / suggestions, then switch highlight not tab
        if (currentIndex == -1) {
        //no section is visible, do nothing
        } else if (currentIndex == visibleSections.length - 1) {
          this.dataFlux.root.openedSection = visibleSections[0].name;
        } else if (currentIndex == 0) {
          this.dataFlux.root.openedSection = visibleSections[visibleSections.length - 1].name;
        } else {
          this.dataFlux.root.openedSection = visibleSections[currentIndex + 1].name;
        }
        break;

      case 'Escape':
        this.dataFlux.root.opened = false;
        break;
      default:
        this.inputChanged(event);
      }
  }

  private increaseHighlightIndex() {

      const visibleSections = this.#getVisibleSections();
      const selectedSection = this.dataFlux.root.openedSection;

      //TODO: if item is divider, it has 0 link slots

      const visibleLinks = visibleSections
        .filter(section => section.name === selectedSection)
        .flatMap((section) => section.uiElements)
        .filter(uiElement => this.dataFlux.root.checkVisibility(uiElement.showWhen))
        .map(uiElement => {
            switch (uiElement.type) {
            case 'popular-queries':
              break;
            }
        });

      //TODO: implement this

      const maxLinkSlot = 10;
      let index = this.dataFlux.root.highlightedIndex + 1;
      if (index >= maxLinkSlot) {
        index = -1;
      }
      this.dataFlux.root.highlightedIndex = index;
      this.dataFlux.root.sideHighlightIndex = -1;
  }

  private decreaseHighlightIndex() {
      let index = this.dataFlux.root.highlightedIndex - 1;
      if (index <= -1) {
        index = 10;
      }
      this.dataFlux.root.highlightedIndex = index;
      this.dataFlux.root.sideHighlightIndex = -1;
  }

  private inputChanged: (event: KeyboardEvent) => void = debounce((event: KeyboardEvent) => {
      this.inputChangedRaw(event);
  }, GuidedV2Defaults.debounceTimeMs);

  /**
   * Actual inputChanged method, to be used wrapped with debounce using `inputChanged` method
   * @param event
   */
  private inputChangedRaw(event: KeyboardEvent) {

      const target = event.target as HTMLInputElement;
      const tagName = target.tagName.toLowerCase();

      if (tagName === 'input' || tagName === 'qds-input') {
        this.dataFlux.root.searchTerm = target.value;
      }
  }

  @Listen('reset')
  resetHandler() {
      this.dataFlux.root.searchTerm = '';
  }

  @Listen('submit', {capture: true})
  submitHandler(event: KeyboardEvent) {
      // /* eslint-disable-next-line no-console */
      // console.log('submitHandler', event);

      //when there is no keyword typed yet but user clicks on the search button,
      //we do nothing. Original behavior is to submit the form - and move the user
      //to the search results page. Empty page - because there is no keyword.
      //in other words, keyword is required
      const input = (this.element.querySelector('qds-input')?.shadowRoot?.querySelector('[type="search"]') as HTMLFormElement)
      || this.element.querySelector('input[name="q"]');

      const isSearchPage = window.location.pathname.includes('/search');
      const form = this.element.querySelector('form');
      if (!form) {
        warn('Form not found');
        return;
      }

      if (input) {
        if (input.value == '') {
          event.preventDefault();
          return;
        }

        input.blur();
      }
      const url = isSearchPage ? new URL(window.location.href) : new URL(form.action);

      if (this.personalizedContent && this.userTypes?.length > 0) {
        event.preventDefault();
        if (!url.searchParams.has('aud')) {
          addUserAudiencesAndRedirect(url.toString(), input.value, this.userTypes);
        }
      }

      this.dataFlux.root.opened = false;
      this.dataFlux.root.showBackdrop = false;
      this.dataFlux.root.mouseHoversGuidedPanel = false;
      //TODO: actual submit logic to be done
  }

  @Listen('focus', {capture: true})
  focusHandler(event: Event) {
      this._focusHandler(event);
  }

  private _focusHandler = (event: Event) => {
      const target: HTMLElement = event.target as HTMLElement;

      const sourceTagName = target.tagName.toLowerCase();
      if (['input', 'qds-input'].includes(sourceTagName)) {
        this.dataFlux.root.opened = true;
        this.dataFlux.root.showBackdrop = this.strategy.details.useBackdrop;
      }
  };

  @Listen('blur', {capture: true, passive: false})
  blurHandler(event: FocusEvent) {
      this._blurHandler(event);
  };

  private _blurHandler = (event: FocusEvent) => {

      const isFocusInGuided = this.element.contains(event.relatedTarget as Node);

      if (
        !isFocusInGuided &&
      !this.dataFlux.root.mouseHoversGuidedPanel /*  && e?.srcElement?.localName !==
       'uiaas-smart-country-selector-banner' */
      && isTargetInput(event)
      ) {
        this.dataFlux.root.opened = false;
        this.dataFlux.root.showBackdrop = false;
      }
  };

  @Listen('search', {capture: true})
  searchHandler(_event: KeyboardEvent) {
      // /* eslint-disable-next-line no-console */
      // console.log('searchHandler', event);
  }

  @Listen('simpleClosePane', {target: 'window'})
  handleB2cClosePane(e?: Event): void {
      e?.stopPropagation?.();
      this.dataFlux.root.showBackdrop = false;
      this.dataFlux.root.opened = false;
  }

  @Listen('guidedV2ClosePane')
  handleCancel(_ev) {
      this.removeBackdrop();
  }

  private removeBackdrop = (e?: Event): void => {
      e?.stopPropagation?.();
      const searchMenuWrapper = document.querySelector('.search-menu-wrapper');
      const cancelButton = document.getElementById('search-bar__cancel');

      this.dataFlux.root.showBackdrop = false;
      this.dataFlux.root.opened = false;

      if (this.strategy.details.useBackdrop && searchMenuWrapper) {
        searchMenuWrapper.classList.remove('show-search-above-mobile-header');

        if (this.strategy.details.enableBackgroundScroll) {
          document.querySelector('body').classList.remove('guided-v2__no-scroll');
        }
        if (cancelButton) {
          cancelButton.style.display = 'none';
        }
      }
  };

  /**
   * Technical event, sent when component is first mounted into DOM
   */
  @Event({
    eventName: 'guidedV2Mount',
    composed: true,
    cancelable: false,
    bubbles: true
  }) eventEmitterGuidedSearchMount: EventEmitter;

  /**
   * Technical event. Sent when ajax returns results
   */
  @Event({
    eventName: 'guidedV2SearchLoaded',
    composed: true,
    cancelable: true,
    bubbles: true
  }) eventEmitterGuidedSearchLoaded: EventEmitter<GuidedSearchLoaded>;

  //event can be default prevented by the page, if its not - we redirect the user
  /**
   * Technical event. Sent when user clicks a link or submits the form
   */
  @Event({
    eventName: 'guidedV2:SearchCompleted',
    composed: true,
    cancelable: true,
    bubbles: true
  }) eventEmitterGuidedSearchCompleted: EventEmitter<GuidedSearchCompleted>;

  /**
   * Sent up the DOM when user types
   */
  @Event({
    eventName: 'guidedV2:UserTyping',
    composed: true,
    cancelable: true,
    bubbles: true
  }) eventEmitterGuidedSearchUserTyping: EventEmitter<GuidedSearchUserTyping>;


  /**
   * Sent up the DOM when user changes the highlighted link with mouse or keyboard
   */
  @Event({
    eventName: 'guidedV2:UserHighlighting',
    composed: true,
    cancelable: true,
    bubbles: true
  }) eventEmitterGuidedSearchLinkHighlighting: EventEmitter<GuidedSearchHighlighting>;

  /**
   * Technical event, reported when user clicks in a history-based quick-link
   */
  @Event({
    eventName: 'guidedV2:HistoryClicked',
    composed: true,
    cancelable: true,
    bubbles: true
  }) eventEmitterGuidedSearchHistoryClicked: EventEmitter<string>;

  /**
   * Send up the DOM when user clikced link on footer with mouse or keyboard
   */
  @Event({
    eventName: 'guidedV2:FooterBannerClicked',
    composed: true,
    cancelable: true,
    bubbles: true
  }) eventEmitterGuidedSearchFooterBannerClicked: EventEmitter<{ url: string }>;

  /**
   * Event emitted when a popular keyword is clicked by the user.
   */
  @Event({
    eventName: 'guidedV2:PopularKeywordClicked',
    composed: true,
    cancelable: true,
    bubbles: true
  }) eventEmitterGuidedSearchPopularKeywordClicked: EventEmitter<void>;

  /**
   * Event emitted when a suggestion keyword is clicked by the user.
   */
  @Event({
    eventName: 'guidedV2:SuggestionKeywordClicked',
    composed: true,
    cancelable: true,
    bubbles: true
  }) eventEmitterGuidedSuggestionKeywordClicked: EventEmitter<void>;

  /**
   * Event emitted when the guided search section is changed by the user.
   */
  @Event({
    eventName: 'guidedV2:SectionChanged',
    composed: true,
    cancelable: true,
    bubbles: true
  }) eventEmitterGuidedSearchSectionChanged: EventEmitter<void>;


  @Event({
    eventName: 'ADD_TO_MY_CART',
    bubbles: true,
  })
    addToCartClicked: EventEmitter<Object>;

  /**
   * Event emitted when the search bar is clicked by the user.
   */
  @Event({
    eventName: 'guidedV2:SearchBarClicked',
    composed: true,
    cancelable: true,
    bubbles: true
  }) eventEmitterGuidedSearchSearchBarClicked: EventEmitter<void>;

  @Event({
    eventName: 'guidedV2:SelectedSuggestionClicked',
    composed: true,
    cancelable: true,
    bubbles: true
  }) eventEmitterGuidedSelectedSuggestionClicked: EventEmitter<void>;

  /**
   * Event emitters, to be passed as part of State, to be used by other components
   */
  private eventEmitters: GuidedV2Emitters = () => {
      return {
        mount: this.eventEmitterGuidedSearchMount,
        loaded: this.eventEmitterGuidedSearchLoaded,
        completed: this.eventEmitterGuidedSearchCompleted,
        userTyping: this.eventEmitterGuidedSearchUserTyping,
        userHighlighting: this.eventEmitterGuidedSearchLinkHighlighting,
        sectionChanged: this.eventEmitterGuidedSearchSectionChanged,
        historyClicked: this.eventEmitterGuidedSearchHistoryClicked,
        bannerClicked: this.eventEmitterGuidedSearchFooterBannerClicked,
        suggestionClicked: this.eventEmitterGuidedSearchCompleted,
        popularKeywordClicked: this.eventEmitterGuidedSearchPopularKeywordClicked,
        selectedSuggestionClicked: this.eventEmitterGuidedSelectedSuggestionClicked,
        addToMyCart: this.addToCartClicked,
      } as const;
  };


  // noinspection JSUnusedGlobalSymbols
  /**
   * Called once just after the component is first connected to the DOM. Since
   * this method is only called once, it's a good place to load data
   * asynchronously and to set up the state without triggering extra re-renders.
   */
  componentWillLoad() {
      this.eventEmitterGuidedSearchMount.emit();
      this.audiences = this.supportedAudiences.split(',');
      this.userTypes = getUserTypes(this.audiences);

      // Initialize searchTerm from URL query param 'q' if present
      if (!this.dataFlux.root.searchTerm) {
        try {
          const qParam = new URL(window.location.href).searchParams.get('q');
          if (qParam) {
            this.dataFlux.root.searchTerm = qParam;
          }
        } catch (e) {
          // ignore if URL parsing fails
        }
      }
  }

  // noinspection JSUnusedGlobalSymbols
  /**
   * Called once just after the component is fully loaded and the first render() occurs.
   */
  componentDidLoad() {
      //console.log('GuidedV2 componentDidLoad');
      this.eventEmitterGuidedSearchMount.emit();

      this.dataFlux.root.openedSection = this.initialSection;

  }

  // noinspection JSUnusedGlobalSymbols
  /**
   * Called before every render().
   *
   * A promise can be returned, that can be used to wait for the upcoming render.
   */
  componentWillRender() {

  }

  /**
   * Called just after the component updates. It's never called during the first render().
   */
  componentDidUpdate() {

  }

  /**
   * This hook is called when a component's Prop or State property changes and a
   * rerender is about to be requested. This hook receives three arguments: the
   * new value, the old value and the name of the changed state. It should return
   * a boolean to indicate if the component should rerender (true) or not (false).
   */
  componentShouldUpdate(newValue: unknown, oldValue: unknown, propName: string) {
      debug('componentShouldUpdate', {newValue, oldValue, propName});
  }

  @Method()
  async reloadComponent() {
      const strategies = StrategiesFactory.createStrategy(this.element);
      // debugger;
      this.strategy = strategies.for<GuidedV2StrategyService>(GUIDED_V2_SERVICE);
      this.dataFlux.root.schema = this.strategy.getSchema();
      info('Strategy reloaded', this.strategy);
  }

  connectedCallback() {

      debug('Guided Search V2 - connectedCallback executed');

      const strategies = StrategiesFactory.createStrategy(this.element);
      // debugger;
      this.strategy = strategies.for<GuidedV2StrategyService>(GUIDED_V2_SERVICE);

      //when strategy changes, update the internal copy of it
      this.eventHandlers.push(
        this.strategy.onStrategyDetailsChange((newStrategy) => {
            this.strategy = newStrategy;
            this.dataFlux.root.schema = this.strategy.getSchema();
        }));

      // console.log("this.strategy",this.strategy);
      // const qdsinput = this.element.querySelector('qds-input');
      // const callback = ()=>{};
      // qdsinput.addEventListener('qdsBlur',callback);
      // this.eventHandlers.push(()=>{
      //     qdsinput.removeEventListener('qdsBlur',callback);
      // });

      this.dataFlux.root.schema = this.strategy.getSchema();

      this.dataFlux.root.labelProvider = (a, b) => this.labelProvider(a, b);

      this.updateRtlStatus();

      this.eventHandlers.push(this.dataFlux.subscribe('searchTerm', async (jsonPathKey, newValue) => {

          info(`Dataflux subscription: ${jsonPathKey} changed `, {newValue, jsonPathKey});

          await this.triggerQuery();

          this.eventEmitterGuidedSearchUserTyping.emit({
            term: newValue,
          });

      }, z.string(), undefined, true));


      this.inputChanged = debounce((event: KeyboardEvent) => {
          this.inputChangedRaw(event);
      }, GuidedV2Defaults.debounceTimeMs);

      const ku = (event) => this.keyDownHandler(event);
      const kd = (event) => this.keyUpHandler(event);
      this.element.addEventListener('keydown', ku);
      this.element.addEventListener('keyup', kd);

      this.eventHandlers.push(() => {
          this.element.removeEventListener('keydown', ku);
          this.element.removeEventListener('keyup', kd);
      });

      const qdsInput = this.element.querySelector('qds-input');
      if (qdsInput) {
        const bh = (event) => this._blurHandler(event);
        const fh = (event) => this._focusHandler(event);
        qdsInput.addEventListener('qdsBlur', bh);
        qdsInput.addEventListener('qdsFocus', fh);

        this.eventHandlers.push(() => {
            qdsInput.removeEventListener('qdsBlur', bh);
            qdsInput.removeEventListener('qdsFocus', fh);
        });
      }


  }

  disconnectedCallback() {
      DataFlux.unsubscribeAll(this.eventHandlers);
  }
}
